import { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Button, Space, Input, message, Card, Statistic, Row, Col, InputNumber } from 'antd';
import { ReloadOutlined, SearchOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { apiKeyAPI } from '../services/api';
import { useSearchParams } from 'react-router-dom';

const APIKeys = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchEmail, setSearchEmail] = useState('');
  const [searchUserId, setSearchUserId] = useState('');
  const [searchApiKey, setSearchApiKey] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const [totalUsageAll, setTotalUsageAll] = useState(0);

  // 统一的获取数据函数
  const fetchData = useCallback(async (page, pageSize, email, userId, apiKey, sortField, sortOrd) => {
    setLoading(true);
    try {
      const params = {};
      if (email) params.email = email;
      if (userId) params.user_id = userId;
      if (apiKey) params.api_key = apiKey;
      if (sortField) {
        params.sort_by = sortField;
        params.sort_order = sortOrd;
      }

      const response = await apiKeyAPI.getList(page, pageSize, params);
      if (response.code === 0) {
        setData(response.data.list || []);
        setPagination({ current: response.data.page, pageSize: response.data.page_size, total: response.data.total });
        // 使用后端返回的总用量（所有符合条件的数据）
        setTotalUsageAll(parseFloat(response.data.total_usage || 0));
      }
    } catch (error) {
      console.error('获取 API Keys 失败:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始化加载
  useEffect(() => {
    const userIdFromUrl = searchParams.get('user_id');
    if (userIdFromUrl) {
      setSearchUserId(userIdFromUrl);
      fetchData(1, 20, '', userIdFromUrl, '', '', '');
    } else {
      fetchData(1, 20, '', '', '', '', '');
    }
  }, []);

  // 搜索
  const handleSearch = () => {
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, searchEmail, searchUserId, searchApiKey, sortBy, sortOrder);
  };

  // 重置
  const handleReset = () => {
    setSearchEmail('');
    setSearchUserId('');
    setSearchApiKey('');
    setSortBy('');
    setSortOrder('');
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, '', '', '', '', '');
  };

  // 清除筛选
  const handleClearFilter = () => {
    setSearchUserId('');
    searchParams.delete('user_id');
    setSearchParams(searchParams);
    fetchData(1, pagination.pageSize, searchEmail, '', searchApiKey, sortBy, sortOrder);
  };

  // 表格变化（分页、排序）
  const handleTableChange = (newPagination, filters, sorter) => {
    const newSortBy = sorter.field || '';
    const newSortOrder = sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : '';
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    fetchData(newPagination.current, newPagination.pageSize, searchEmail, searchUserId, searchApiKey, newSortBy, newSortOrder);
  };

  // 切换状态
  const handleToggle = async (id) => {
    try {
      const response = await apiKeyAPI.toggle(id);
      if (response.code === 0) {
        message.success(response.data.is_active ? '已启用' : '已禁用');
        fetchData(pagination.current, pagination.pageSize, searchEmail, searchUserId, searchApiKey, sortBy, sortOrder);
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 280, render: (id) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</span> },
    { title: '用户邮箱', dataIndex: 'user', key: 'email', width: 200, render: (user) => user?.email || '-' },
    { title: 'Key 名称', dataIndex: 'name', key: 'name', width: 120 },
    { title: 'API Key', dataIndex: 'api_key', key: 'api_key', width: 220, render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text ? text.substring(0, 12) + '...' + text.substring(text.length - 6) : '-'}</span> },
    { title: '累计用量', dataIndex: 'total_usage', key: 'total_usage', width: 130, sorter: true, render: (val) => {
      // total_usage 后端已转换为美元
      const usageInDollar = parseFloat(val || 0).toFixed(4);
      return <span style={{ color: val > 0 ? '#f59e0b' : '#999', fontWeight: val > 0 ? 500 : 400 }}>$ {usageInDollar}</span>;
    }},
    { title: '状态', dataIndex: 'is_active', key: 'is_active', width: 80, render: (isActive) => <Tag color={isActive ? 'green' : 'red'}>{isActive ? '启用' : '禁用'}</Tag> },
    { title: '折扣率', dataIndex: 'discount_rate', key: 'discount_rate', width: 130,
      render: (rate, record) => {
        const rateVal = record.discount_rate ?? rate ?? 1;
        const discountLabel = rateVal >= 1 ? '原价' : (rateVal * 10).toFixed(1) + '折';
        return (
        <Space size={4}>
          <Tag color={rateVal >= 1 ? 'default' : 'orange'}>{discountLabel}</Tag>
          <InputNumber size="small" defaultValue={rateVal} min={0.1} max={1} step={0.05} style={{width:60}}
            onBlur={async (e) => {
              const v = parseFloat(e.target.value);
              if (v > 0 && v <= 1 && v !== rateVal) {
                await apiKeyAPI.update(record.id, { discount_rate: v });
                setData(prev => prev.map(item => item.id === record.id ? {...item, discount_rate: v} : item));
                message.success('已更新为' + (v >= 1 ? '原价' : (v * 10).toFixed(1) + '折'));
              }
            }} />
        </Space>
      )},
    },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 150, sorter: true, render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-' },
    { title: '最近使用', dataIndex: 'last_used_at', key: 'last_used_at', width: 150, sorter: true, render: (text) => text ? new Date(text).toLocaleString('zh-CN') : '-' },
    { title: '操作', key: 'actions', width: 80, fixed: 'right', render: (_, record) => <Button type="link" size="small" icon={<SwapOutlined />} onClick={() => handleToggle(record.id)}>{record.is_active ? '禁用' : '启用'}</Button> },
  ];

  const activeKeys = data.filter(d => d.is_active).length;

  return (
    <div className="fit-screen-page">
      {/* 页面标题 + 统计卡片 */}
      <div className="page-header" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 8 }}>
        <h2 className="page-title" style={{ marginBottom: 8 }}>API Key 管理</h2>
        <Row gutter={12}>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="Key 总数" value={pagination.total} /></Card></Col>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="本页启用数" value={activeKeys} valueStyle={{ color: '#52c41a' }} /></Card></Col>
          <Col span={8}><Card size="small" styles={{ body: { padding: '8px 12px' } }}><Statistic title="累计总用量" value={totalUsageAll.toFixed(4)} prefix="$" valueStyle={{ color: totalUsageAll > 0 ? '#f59e0b' : '#999' }} /></Card></Col>
        </Row>
      </div>

      {/* 搜索栏 */}
      <div className="search-bar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {searchParams.get('user_id') && (
          <Tag closable onClose={handleClearFilter} style={{ marginRight: 8 }}>
            筛选用户: {searchParams.get('user_id')}
          </Tag>
        )}
        <Input placeholder="搜索用户邮箱" value={searchEmail} onChange={(e) => setSearchEmail(e.target.value)} style={{ width: 200 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Input placeholder="搜索用户 ID" value={searchUserId} onChange={(e) => setSearchUserId(e.target.value)} style={{ width: 140 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Input placeholder="搜索 API Key" value={searchApiKey} onChange={(e) => setSearchApiKey(e.target.value)} style={{ width: 220 }} allowClear disabled={!!searchParams.get('user_id')} />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
      </div>

      {/* 表格容器 */}
      <div className="table-container">
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400, y: 600 }}
          pagination={{ ...pagination, showSizeChanger: true, showQuickJumper: true, showTotal: (total) => `共 ${total} 条` }}
          onChange={handleTableChange}
          size="small"
        />
      </div>
    </div>
  );
};

export default APIKeys;
